import {
  alpha,
  decomposeColor,
  recomposeColor,
  useTheme,
} from '@material-ui/core/styles';

/**
 * How much stronger a tinted fill gets under the pointer (a tonal button's
 * hover): its alpha plus this.
 */
const HOVER_FILL_STEP = 0.08;

/** MUI's emphasis of disabled text, applied to the dialog's text colour. */
const DISABLED_ALPHA = 0.38;

/**
 * The opaque colour a translucent `foreground` shows as over the opaque
 * `background`: a control part that other parts are painted under (a
 * switch's thumb over its track) cannot be see-through.
 * @param {string} foreground hex, rgb() or rgba() colour
 * @param {string} background hex or rgb() colour
 * @returns {string} an rgb() colour
 */
export const flattenColor = (foreground, background) => {
  const top = decomposeColor(foreground);
  const bottom = decomposeColor(background);
  const topAlpha = top.values.length > 3 ? top.values[3] : 1;
  return recomposeColor({
    type: 'rgb',
    values: top.values
      .slice(0, 3)
      .map((value, index) =>
        Math.round(value * topAlpha + bottom.values[index] * (1 - topAlpha))
      ),
  });
};

/**
 * A tinted fill one step stronger (its alpha plus 0.08), for the hover of
 * a tonal button.
 * @param {string} fill an rgba() colour
 * @returns {string}
 */
export const strongerFill = (fill) => {
  const { values } = decomposeColor(fill);
  const fillAlpha = values.length > 3 ? values[3] : 1;
  return recomposeColor({
    type: 'rgba',
    values: [
      values[0],
      values[1],
      values[2],
      Math.min(1, Math.round((fillAlpha + HOVER_FILL_STEP) * 100) / 100),
    ],
  });
};

/**
 * A colour at half its own alpha: a disabled field's outline, half as
 * present as an enabled one's.
 * @param {string} color
 * @returns {string}
 */
const halfAlpha = (color) => {
  const { values } = decomposeColor(color);
  const colorAlpha = values.length > 3 ? values[3] : 1;
  return alpha(color, Math.round(colorAlpha * 50) / 100);
};

/**
 * The redesigned dialog's colours, by role: every one is a
 * theme.component.redesignDialog* token of the current theme (built by
 * redesignDialogComponentTokens in the three theme files) or derived from
 * them (`disabled`, `fieldOutlineDisabled`, the `*FillHover` fills,
 * `thumb`). A cool slate in
 * the classic and the dark theme, white and cool greys in the light theme;
 * the accent is Lixani's cyan.
 *
 * Use it for anything a dialog built on RedesignDialog paints itself (a PDF
 * pane's toolbar, a row inside a section), so that it matches the dialog in
 * every theme.
 * @example
 * const useStyles = makeStyles((theme) => {
 *   const colors = getRedesignDialogColors(theme);
 *   return { toolbar: { backgroundColor: colors.surfaceRaised, color: colors.muted } };
 * });
 * @param {Object} theme MUI theme
 * @returns {{
 *   backdrop: string, surface: string, surfaceRaised: string, well: string,
 *   border: string, edge: string, shadow: string,
 *   text: string, muted: string, disabled: string,
 *   fieldOutline: string, fieldOutlineHover: string,
 *   fieldOutlineDisabled: string,
 *   hoverFill: string, selectedFill: string,
 *   accent: string, accentText: string,
 *   success: string, successFill: string, successFillHover: string,
 *   warning: string, warningFill: string, warningFillHover: string,
 *   danger: string, dangerFill: string, dangerFillHover: string,
 *   thumb: string,
 * }}
 */
export const getRedesignDialogColors = (theme) => {
  const {
    redesignDialogBackdrop,
    redesignDialogSurface,
    redesignDialogSurfaceRaised,
    redesignDialogWell,
    redesignDialogBorder,
    redesignDialogEdge,
    redesignDialogShadow,
    redesignDialogMuted,
    redesignDialogFieldOutline,
    redesignDialogFieldOutlineHover,
    redesignDialogHoverFill,
    redesignDialogSelectedFill,
    redesignDialogAccent,
    redesignDialogSuccess,
    redesignDialogWarning,
    redesignDialogDanger,
  } = theme.component;
  const surface = redesignDialogSurface.backgroundColor;
  const text = redesignDialogSurface.color;
  const muted = redesignDialogMuted.color;
  const fieldOutline = redesignDialogFieldOutline.borderColor;

  return {
    // The dimmed page behind the dialog.
    backdrop: redesignDialogBackdrop.backgroundColor,
    // The paper: header, body and footer.
    surface,
    // A bar or a menu of the dialog's own, above the surface.
    surfaceRaised: redesignDialogSurfaceRaised.backgroundColor,
    // A fill inside the paper: a split dialog's side pane, a field group.
    well: redesignDialogWell.backgroundColor,
    // Lines inside the dialog: header and footer rules, between panes.
    border: redesignDialogBorder.borderColor,
    // The paper's own hairline.
    edge: redesignDialogEdge.borderColor,
    shadow: redesignDialogShadow.boxShadow,
    text,
    muted,
    // Disabled text and controls: the text colour at MUI's 0.38.
    disabled: alpha(text, DISABLED_ALPHA),
    // A field's outline and the neutral outlined button's (3:1 on surface).
    fieldOutline,
    fieldOutlineHover: redesignDialogFieldOutlineHover.borderColor,
    fieldOutlineDisabled: halfAlpha(fieldOutline),
    hoverFill: redesignDialogHoverFill.backgroundColor,
    selectedFill: redesignDialogSelectedFill.backgroundColor,
    accent: redesignDialogAccent.backgroundColor,
    accentText: redesignDialogAccent.color,
    success: redesignDialogSuccess.color,
    successFill: redesignDialogSuccess.backgroundColor,
    successFillHover: strongerFill(redesignDialogSuccess.backgroundColor),
    warning: redesignDialogWarning.color,
    warningFill: redesignDialogWarning.backgroundColor,
    warningFillHover: strongerFill(redesignDialogWarning.backgroundColor),
    danger: redesignDialogDanger.color,
    dangerFill: redesignDialogDanger.backgroundColor,
    dangerFillHover: strongerFill(redesignDialogDanger.backgroundColor),
    // An unchecked switch's thumb: the muted colour made opaque over the
    // surface, since the track shows under it.
    thumb: flattenColor(muted, surface),
  };
};

/**
 * getRedesignDialogColors for the current theme, for inline styles.
 * @example
 * const colors = useRedesignDialogColors();
 * return <span style={{ color: colors.muted }}>{t('expense.vat')}</span>;
 * @returns {ReturnType<typeof getRedesignDialogColors>}
 */
export const useRedesignDialogColors = () =>
  getRedesignDialogColors(useTheme());
